import React, { useState } from "react";
import { COMPANY_CONFIG } from "../data/companyConfig";
import {
    Zap,
    ShieldCheck,
    ArrowRight,
    Smartphone,
    Droplets,
    Tv,
    Flame,
    Car,
    Sparkles,
} from "lucide-react";

export const Hero = ({ onExploreServices, onContactClick, onSignIn }) => {
    // Interactive Live Payment Switch state in Hero
    const [activeUtility, setActiveUtility] = useState("electricity");
    const [consumerInput, setConsumerInput] = useState("");
    const [isFetching, setIsFetching] = useState(false);
    const [fetchedData, setFetchedData] = useState(null);

    const utilityConfig = {
        electricity: {
            label: "Electricity",
            icon: Zap,
            color: "text-amber-500 bg-amber-50 border-amber-200",
            activeColor: "bg-blue-600 text-white",
            placeholder: "e.g. 1029384756 (TSSPDCL / BESCOM)",
            provider: "TSSPDCL Telangana Power",
            fieldLabel: "Consumer Service Number",
            defaultAmount: 940,
        },
        mobile: {
            label: "Mobile",
            icon: Smartphone,
            color: "text-blue-600 bg-blue-50 border-blue-200",
            activeColor: "bg-blue-600 text-white",
            placeholder: "e.g. 9848012345 (10-digit mobile)",
            provider: "Jio / Airtel Prepaid",
            fieldLabel: "Mobile Number",
            defaultAmount: 349,
        },
        dth: {
            label: "DTH",
            icon: Tv,
            color: "text-purple-600 bg-purple-50 border-purple-200",
            activeColor: "bg-blue-600 text-white",
            placeholder: "e.g. 1092837465 (Subscriber ID)",
            provider: "Tata Play / Airtel DTH",
            fieldLabel: "Subscriber / SmartCard ID",
            defaultAmount: 480,
        },
        water: {
            label: "Water",
            icon: Droplets,
            color: "text-cyan-600 bg-cyan-50 border-cyan-200",
            activeColor: "bg-blue-600 text-white",
            placeholder: "e.g. HMWS-892301 (CAN Number)",
            provider: "HMWS&SB Hyderabad Water",
            fieldLabel: "CAN / Connection ID",
            defaultAmount: 380,
        },
        gas: {
            label: "Gas & LPG",
            icon: Flame,
            color: "text-orange-600 bg-orange-50 border-orange-200",
            activeColor: "bg-blue-600 text-white",
            placeholder: "e.g. 1726354890 (Customer ID)",
            provider: "Indane / Bharat Gas",
            fieldLabel: "LPG ID / Consumer No",
            defaultAmount: 890,
        },
        fastag: {
            label: "FASTag",
            icon: Car,
            color: "text-emerald-600 bg-emerald-50 border-emerald-200",
            activeColor: "bg-blue-600 text-white",
            placeholder: "e.g. TS09FA1234 (Vehicle Reg No)",
            provider: "NETC FASTag (All Banks)",
            fieldLabel: "Vehicle Registration No",
            defaultAmount: 500,
        },
    };

    const currentConfig = utilityConfig[activeUtility];

    const handleFetchBill = (e) => {
        e.preventDefault();
        if (!consumerInput.trim()) return;

        setIsFetching(true);
        setTimeout(() => {
            setIsFetching(false);
            setFetchedData({
                customerName: "Sriram Lakshmi R.",
                billNumber: `BILL-${Math.floor(100000 + Math.random() * 900000)}`,
                amount: currentConfig.defaultAmount,
                dueDate: "22 Sep 2026",
                provider: currentConfig.provider,
            });
        }, 600);
    };

    return (
        <section
            id="hero-section"
            className="relative overflow-hidden bg-gradient-to-b from-white via-blue-50/30 to-emerald-50/20 text-slate-900 pt-12 pb-16 lg:pt-16 lg:pb-24 border-b border-slate-200"
        >
            {/* Background ambient lighting in Blue and Green */}
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute top-1/3 right-10 w-96 h-96 bg-emerald-400/10 rounded-full blur-3xl pointer-events-none" />

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    {/* Left Column: Payswitch-style Hero Messaging */}
                    <div className="lg:col-span-7 space-y-6 text-center lg:text-left">
                        {/* The Ultimate Payment Switch Tagline (Blue & Green Accents) */}
                        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-xs font-bold tracking-wider uppercase shadow-2xs">
                            <Sparkles className="w-3.5 h-3.5 text-emerald-600 animate-pulse" />
                            <span>The Ultimate Payment Switch By Global Bizness</span>
                        </div>

                        <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-tight font-['Outfit',sans-serif]">
                            Empowering Your{" "}
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-700 via-blue-600 to-emerald-600">
                                Financial Freedom
                            </span>
                        </h1>

                        <p className="text-sm sm:text-base text-slate-600 max-w-xl mx-auto lg:mx-0 leading-relaxed font-normal">
                            Simplifying Utility Payments for Everyone Across India. We
                            revolutionize utility bill settlements by creating an accessible,
                            seamless, and efficient payment ecosystem through technological
                            innovation, trust, and a strong offline agent network.
                        </p>

                        {/* Action Buttons: Blue and Green */}
                        <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3.5 pt-2">
                            <button
                                onClick={onExploreServices}
                                className="px-6 py-3.5 rounded-xl font-bold text-xs sm:text-sm text-white bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-600/20 transition-all flex items-center gap-2 group cursor-pointer"
                                id="hero-explore-services-btn"
                            >
                                <span>Explore Services</span>
                                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </button>

                            <button
                                onClick={onContactClick}
                                className="px-6 py-3.5 rounded-xl font-bold text-xs sm:text-sm text-white bg-emerald-600 hover:bg-emerald-500 shadow-lg shadow-emerald-600/20 transition-all flex items-center gap-2 cursor-pointer"
                                id="hero-join-today-btn"
                            >
                                <span>Join Today</span>
                                <ArrowRight className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Trust Metrics Ribbon (White Cards with Blue & Green Accents) */}
                        <div className="pt-6 border-t border-slate-200 grid grid-cols-2 sm:grid-cols-4 gap-4 text-left">
                            <div className="bg-white border border-slate-200/90 p-4 rounded-2xl shadow-xs">
                                <div className="text-xl sm:text-2xl font-extrabold text-blue-700 font-['Outfit',sans-serif]">
                                    99.8%
                                </div>
                                <div className="text-[11px] text-slate-500 font-medium">
                                    Success Rate
                                </div>
                            </div>
                            <div className="bg-white border border-slate-200/90 p-4 rounded-2xl shadow-xs">
                                <div className="text-xl sm:text-2xl font-extrabold text-emerald-600 font-['Outfit',sans-serif]">
                                    Instant
                                </div>
                                <div className="text-[11px] text-slate-500 font-medium">
                                    BBPS Settlement
                                </div>
                            </div>
                            <div className="bg-white border border-slate-200/90 p-4 rounded-2xl shadow-xs">
                                <div className="text-xl sm:text-2xl font-extrabold text-blue-700 font-['Outfit',sans-serif]">
                                    256-Bit
                                </div>
                                <div className="text-[11px] text-slate-500 font-medium">
                                    SSL Encryption
                                </div>
                            </div>
                            <div className="bg-white border border-slate-200/90 p-4 rounded-2xl shadow-xs">
                                <div className="text-xl sm:text-2xl font-extrabold text-emerald-600 font-['Outfit',sans-serif]">
                                    24/7
                                </div>
                                <div className="text-[11px] text-slate-500 font-medium">
                                    Hyderabad Helpdesk
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Column: The Ultimate Payment Switch Interactive Console (Crisp White Card) */}
                    <div className="lg:col-span-5">
                        <div className="relative mx-auto max-w-md bg-white rounded-3xl p-6 sm:p-7 shadow-xl border border-slate-200 space-y-5">
                            {/* Card Header */}
                            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                                <div className="flex items-center gap-2.5">
                                    <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-200 text-blue-600 flex items-center justify-center font-bold">
                                        <Zap className="w-5 h-5 text-blue-600" />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-sm text-slate-900 font-['Outfit',sans-serif]">
                                            The Payment Switch
                                        </h3>
                                        <p className="text-[11px] text-slate-500">
                                            Instant Utility Settlement
                                        </p>
                                    </div>
                                </div>
                                <span className="text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-1 rounded-full flex items-center gap-1">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                    Live Rails
                                </span>
                            </div>

                            {/* Utility Category Switch Selector */}
                            <div>
                                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                                    Select Utility Switch
                                </label>
                                <div className="grid grid-cols-3 gap-2">
                                    {Object.keys(utilityConfig).map((key) => {
                                        const item = utilityConfig[key];
                                        const ItemIcon = item.icon;
                                        const isSelected = activeUtility === key;
                                        return (
                                            <button
                                                key={key}
                                                type="button"
                                                onClick={() => {
                                                    setActiveUtility(key);
                                                    setFetchedData(null);
                                                }}
                                                className={`p-2.5 rounded-xl border text-center transition-all flex flex-col items-center gap-1.5 cursor-pointer text-xs font-semibold ${
                                                    isSelected
                                                        ? "bg-blue-600 text-white border-blue-600 shadow-md shadow-blue-600/20"
                                                        : "bg-slate-50 text-slate-700 border-slate-200 hover:bg-blue-50 hover:text-blue-700"
                                                }`}
                                            >
                                                <ItemIcon className="w-4 h-4" />
                                                <span className="text-[11px] truncate w-full">
                                                    {item.label}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Quick Input Form */}
                            <form onSubmit={handleFetchBill} className="space-y-3 pt-1">
                                <div>
                                    <div className="flex justify-between items-center mb-1">
                                        <label className="text-xs font-bold text-slate-700">
                                            {currentConfig.fieldLabel}
                                        </label>
                                        <span className="text-[10px] text-blue-600 font-medium">
                                            {currentConfig.provider}
                                        </span>
                                    </div>
                                    <input
                                        type="text"
                                        required
                                        value={consumerInput}
                                        onChange={(e) => setConsumerInput(e.target.value)}
                                        placeholder={currentConfig.placeholder}
                                        className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 placeholder:text-slate-400 focus:outline-hidden focus:bg-white focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
                                    />
                                </div>

                                {!fetchedData && (
                                    <button
                                        type="submit"
                                        disabled={isFetching}
                                        className="w-full py-2.5 px-4 rounded-xl font-bold text-xs text-white bg-blue-600 hover:bg-blue-700 transition-colors shadow-md flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                                    >
                                        {isFetching ? (
                                            <span>Fetching Bill Details...</span>
                                        ) : (
                                            <>
                                                <span>Fetch Bill & Verify Amount</span>
                                                <ArrowRight className="w-3.5 h-3.5" />
                                            </>
                                        )}
                                    </button>
                                )}
                            </form>

                            {/* Verified Bill Card & Pay Trigger (Emerald Green Styling).
                                Paying requires a signed-in partner account, so the CTA hands
                                off to the real sign-in screen. */}
                            {fetchedData && (
                                <div className="p-4 bg-emerald-50/50 rounded-2xl border border-emerald-200 space-y-3 animate-in fade-in duration-200">
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="text-slate-500">Customer:</span>
                                        <span className="font-bold text-slate-900">
                                            {fetchedData.customerName}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="text-slate-500">Bill ID:</span>
                                        <span className="font-mono text-blue-700 font-semibold">
                                            {fetchedData.billNumber}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="text-slate-500">Due Date:</span>
                                        <span className="text-amber-600 font-semibold">
                                            {fetchedData.dueDate}
                                        </span>
                                    </div>
                                    <div className="pt-2 border-t border-emerald-200/80 flex items-center justify-between">
                                        <span className="text-xs text-slate-700 font-medium">
                                            Payable Amount:
                                        </span>
                                        <span className="text-xl font-black text-emerald-700">
                                            ₹{fetchedData.amount}.00
                                        </span>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={onSignIn}
                                        className="w-full py-3 px-4 rounded-xl font-bold text-xs text-white bg-emerald-600 hover:bg-emerald-500 shadow-lg shadow-emerald-600/30 transition-colors flex items-center justify-center gap-2 cursor-pointer"
                                    >
                                        <ShieldCheck className="w-4 h-4" />
                                        <span>Sign In to Pay ₹{fetchedData.amount}.00</span>
                                    </button>
                                </div>
                            )}

                            {/* Support footnote */}
                            <div className="pt-2 text-center text-xs text-slate-500 bg-slate-50 p-3 rounded-xl border border-slate-200">
                                <span>Helpline: </span>
                                <a
                                    href={`tel:${COMPANY_CONFIG.phoneClean}`}
                                    className="font-bold text-blue-700 hover:underline"
                                >
                                    {COMPANY_CONFIG.phone}
                                </a>
                                <span> • Madhapur, Hyderabad</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
